import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { apiClient } from 'Helpers/network';

import useApiRequest from 'Hooks/useApiRequest';
import { useToast } from 'Hooks/useToast';

const DEFAULT_CRITERIA = {
    budget_id: null,
    department_ids: [],
    include_details: false,
    include_cash: true,
    include_vik: true,
    include_detailed_description: true,
    include_workforce: false,
};

const DepartmentsBudgetsReportContext = createContext();

export const useDepartmentsBudgetsReportContext = () => {
    const context = useContext(DepartmentsBudgetsReportContext);

    if (!context) {
        throw new Error('useDepartmentsBudgetsReportContext must be used within a DepartmentsBudgetsReportContext');
    }

    return context;
};

export const DepartmentsBudgetsReportProvider = ({
    exportReportUrl,
    fetchArchivedReportsUrl,
    deleteArchivedReportUrl,
    sendDepartmentsBudgetsReportEmailUrl,
    showBudgetReviewUrl,
    editBudgetReviewUrl,
    showBudgetSnapshotUrl,
    deleteBudgetReviewUrl,
    deleteBudgetReviewActionUrl,
    downloadArchivedReportUrl,
    budgetVersions,
    departmentsOptions,
    isVikEnabled,
    channelName,
    markUpCategories,
    children,
}) => {
    const [criteria, setCriteria] = useState({
        ...DEFAULT_CRITERIA,
        include_vik: isVikEnabled !== false,
    });

    const fetchArchivedReportsRequest = useApiRequest({ url: fetchArchivedReportsUrl });
    const deleteArchivedReportRequest = useApiRequest({ url: deleteArchivedReportUrl });

    const toast = useToast();

    const configs = useMemo(
        () => ({
            budgetVersions: budgetVersions || [],
            departmentsOptions: departmentsOptions || [],
        }),
        [budgetVersions, departmentsOptions],
    );

    const fetchArchivedReportsData = useCallback(
        (pagination) => {
            const { reset, get } = fetchArchivedReportsRequest;

            reset();

            return get({
                page: pagination.page,
                per_page: pagination.perPage,
            });
        },
        [fetchArchivedReportsRequest.reset, fetchArchivedReportsRequest.get],
    );

    const deleteArchivedReport = useCallback(
        (reportId) => {
            const { delete: deleteRequest } = deleteArchivedReportRequest;

            return deleteRequest(null, { REPORT_CENTRE_EXPORT_ID: reportId });
        },
        [deleteArchivedReportRequest.delete],
    );

    const downloadArchivedReport = useCallback(
        (reportId) => {
            const url = downloadArchivedReportUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId);
            window.open(url, '_blank');
        },
        [downloadArchivedReportUrl],
    );

    const exportReport = () => {
        return apiClient
            .post(exportReportUrl, criteria)
            .then(({ data }) => {
                toast.success($t('The request to generate a report is being processed. This may take a few minutes'));
            })
            .catch((e) => {
                console.log(e);
            });
    };

    const sendReportEmail = useCallback(
        (email) => {
            return apiClient.post(sendDepartmentsBudgetsReportEmailUrl, {
                criteria,
                email,
            });
        },
        [criteria, sendDepartmentsBudgetsReportEmailUrl, toast],
    );

    const deleteBudgetReview = useCallback(
        (reviewId) => {
            return apiClient
                .delete(deleteBudgetReviewUrl.replace('BUDGETS_REVIEW_ID', reviewId))
                .then(() => {
                    toast.success($t('Budget review deleted successfully.'));
                })
                .catch((e) => {
                    console.log(e);
                });
        },
        [deleteBudgetReviewUrl, toast],
    );

    const deleteBudgetReviewAction = useCallback(
        (reviewId, actionId) => {
            const url = deleteBudgetReviewActionUrl.replace('BUDGETS_REVIEW_ID', reviewId).replace('BUDGETS_REVIEW_ACTION_ID', actionId);

            return apiClient
                .delete(url)
                .then(() => {
                    toast.success($t('Review action deleted successfully.'));
                })
                .catch((e) => {
                    console.log(e);
                });
        },
        [deleteBudgetReviewActionUrl, toast],
    );

    const updateCriteria = useCallback((newCriteria) => {
        setCriteria((prevCriteria) => ({
            ...prevCriteria,
            ...newCriteria,
        }));
    }, []);

    const isOptionIncluded = useCallback(
        (optionValue) => {
            return criteria && !!criteria[`include_${optionValue}`];
        },
        [criteria],
    );

    const value = {
        criteria,
        configs,
        isVikEnabled,
        loadingArchivedReports: fetchArchivedReportsRequest.loading,
        archivedReportsData: fetchArchivedReportsRequest.data,
        channelName,
        markUpCategories,
        showBudgetReviewUrl,
        editBudgetReviewUrl,
        showBudgetSnapshotUrl,
        updateCriteria,
        isOptionIncluded,
        exportReport,
        fetchArchivedReportsData,
        deleteArchivedReport,
        downloadArchivedReport,
        sendReportEmail,
        deleteBudgetReview,
        deleteBudgetReviewAction,
    };

    return <DepartmentsBudgetsReportContext.Provider value={value}>{children}</DepartmentsBudgetsReportContext.Provider>;
};

export default DepartmentsBudgetsReportContext;
